import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Stage, Layer, Rect, Circle, Text, Group, Line, Image } from "react-konva";
import { getVariant, getStudentResources } from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Skeleton } from "../components/ui/skeleton";
import { Switch } from "../components/ui/switch";
import {
  ChevronLeft,
  ChevronRight,
  Volume2,
  Home,
  Maximize2,
  Minimize2,
  BookOpen,
  Eye,
  EyeOff,
  Settings,
} from "lucide-react";

// Syllable colors for reading aid
const SYLLABLE_COLORS = ["#3b82f6", "#ef4444", "#22c55e", "#f97316", "#8b5cf6", "#ec4899"];

// Syllabation function (simplified French syllables)
function syllabify(word) {
  if (!word || word.length <= 2) return [word];
  
  const vowels = "aeiouyàâäéèêëïîôùûü";
  const consonantClusters = ["bl", "br", "ch", "cl", "cr", "dr", "fl", "fr", "gl", "gn", "gr", "ph", "pl", "pr", "qu", "sc", "sk", "sl", "sm", "sn", "sp", "st", "sw", "th", "tr", "vr", "wr"];
  
  const syllables = [];
  let current = "";
  const lower = word.toLowerCase();
  
  for (let i = 0; i < word.length; i++) {
    const char = word[i];
    const lowerChar = lower[i];
    current += char;
    
    if (vowels.includes(lowerChar)) {
      // Check if next char is consonant followed by vowel
      if (i < word.length - 2) {
        const next = lower[i + 1];
        const afterNext = lower[i + 2];
        
        if (!vowels.includes(next) && vowels.includes(afterNext)) {
          syllables.push(current);
          current = "";
        }
      } else if (i === word.length - 1) {
        // End of word
        if (syllables.length > 0 && current.length === 1) {
          // Attach final single vowel to previous syllable
          syllables[syllables.length - 1] += current;
          current = "";
        }
      }
    }
  }
  
  if (current) {
    if (syllables.length > 0 && current.length <= 2) {
      syllables[syllables.length - 1] += current;
    } else {
      syllables.push(current);
    }
  }
  
  return syllables.length > 0 ? syllables : [word];
}

// Detect silent letters (simplified French rules)
function detectSilentLetters(word) {
  const silentPositions = [];
  const lower = word.toLowerCase();
  const len = word.length;
  
  // Silent final letters
  const silentFinals = ["e", "s", "t", "d", "x", "p", "g", "z"];
  if (len > 1 && silentFinals.includes(lower[len - 1])) {
    // Check it's not part of a sound
    const lastTwo = lower.slice(-2);
    if (!["er", "ez", "es", "et", "ée"].includes(lastTwo)) {
      silentPositions.push(len - 1);
    }
  }
  
  // Silent 'h'
  for (let i = 0; i < len; i++) {
    if (lower[i] === "h") {
      silentPositions.push(i);
    }
  }
  
  // Silent -ent (verb endings)
  if (lower.endsWith("ent") && len > 4) {
    silentPositions.push(len - 3, len - 2, len - 1);
  }
  
  return silentPositions;
}

/**
 * StudentResourceViewerPage - Viewer for students to see their differentiated resources
 * Displays the variant that was generated specifically for them
 */
export default function StudentResourceViewerPage() {
  const { variantId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const containerRef = useRef(null);
  
  const [variant, setVariant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dimensions, setDimensions] = useState({ width: 350, height: 500 });
  const [scale, setScale] = useState(1);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  
  // Display preferences (can be toggled by student)
  const [displayPrefs, setDisplayPrefs] = useState({
    showColoredSyllables: true,
    showSilentLetters: true,
    showLineAlternation: false,
    highContrast: false,
    fontSize: "normal", // small, normal, large
  });

  useEffect(() => {
    if (variantId) {
      fetchVariant();
    }
  }, [variantId]);

  useEffect(() => {
    const handleResize = () => {
      if (containerRef.current) {
        const container = containerRef.current;
        const maxWidth = container.clientWidth - 32;
        const maxHeight = container.clientHeight - 32;
        
        // Calculate scale to fit
        const scaleX = maxWidth / 350;
        const scaleY = maxHeight / 500;
        const newScale = Math.min(scaleX, scaleY, 1.5);
        
        setDimensions({
          width: 350 * newScale,
          height: 500 * newScale,
        });
        setScale(newScale);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const fetchVariant = async () => {
    setLoading(true);
    try {
      const { data } = await getVariant(variantId);
      setVariant(data);
      
      // Apply display preferences from the variant if available
      if (data?.generated_from_display_preferences) {
        const prefs = data.generated_from_display_preferences;
        setDisplayPrefs({
          showColoredSyllables: prefs.show_colored_syllables ?? true,
          showSilentLetters: prefs.show_silent_letters ?? true,
          showLineAlternation: prefs.show_line_alternation ?? false,
          highContrast: prefs.high_contrast ?? false,
          fontSize: prefs.font_size || "normal",
        });
      }
    } catch (err) {
      console.error("Error fetching variant:", err);
      setError("Cette ressource n'est pas disponible.");
    } finally {
      setLoading(false);
    }
  };

  const handleGoHome = () => {
    navigate("/home");
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  // Render a differentiated text element with reading aids
  const renderDifferentiatedText = (element, key) => {
    const text = element.text || "";
    const baseFontSize = element.fontSize || 20;
    
    // Font size multiplier based on preference
    const fontSizeMultiplier = {
      small: 0.85,
      normal: 1,
      large: 1.25,
    }[displayPrefs.fontSize] || 1;
    
    const fontSize = baseFontSize * scale * fontSizeMultiplier;
    const x = element.x * scale;
    const y = element.y * scale;
    const width = (element.width || 200) * scale;
    
    // If no reading aids, render simple text
    if (!displayPrefs.showColoredSyllables && !displayPrefs.showSilentLetters) {
      return (
        <Text
          key={key}
          x={x}
          y={y}
          text={text}
          fontSize={fontSize}
          fontFamily={element.fontFamily || "Nunito"}
          fill={displayPrefs.highContrast ? "#000000" : (element.fill || "#1e293b")}
          width={width}
          align={element.align || "left"}
          lineHeight={element.lineHeight || 1.6}
          letterSpacing={element.letterSpacing || 0}
        />
      );
    }
    
    // Render with syllable coloring - we need to split into words
    const words = text.split(/(\s+)/);
    const textElements = [];
    let currentX = x;
    let currentY = y;
    let colorIndex = 0;
    
    words.forEach((word, wordIndex) => {
      if (/^\s+$/.test(word)) {
        // It's whitespace
        currentX += fontSize * 0.3 * word.length;
        return;
      }
      
      if (displayPrefs.showColoredSyllables) {
        const syllables = syllabify(word);
        const silentPositions = displayPrefs.showSilentLetters ? detectSilentLetters(word) : [];
        
        let charOffset = 0;
        syllables.forEach((syllable, syllIndex) => {
          const color = SYLLABLE_COLORS[colorIndex % SYLLABLE_COLORS.length];
          colorIndex++;
          
          // Check for silent letters within this syllable
          let syllableText = syllable;
          
          textElements.push(
            <Text
              key={`${key}-${wordIndex}-${syllIndex}`}
              x={currentX}
              y={currentY}
              text={syllableText}
              fontSize={fontSize}
              fontFamily={element.fontFamily || "Nunito"}
              fill={displayPrefs.highContrast ? "#000000" : color}
              fontStyle={displayPrefs.highContrast ? "bold" : "normal"}
            />
          );
          
          // Estimate width and move cursor
          const charWidth = fontSize * 0.55;
          currentX += syllable.length * charWidth;
          charOffset += syllable.length;
        });
      } else {
        // Just render word without syllable coloring
        textElements.push(
          <Text
            key={`${key}-${wordIndex}`}
            x={currentX}
            y={currentY}
            text={word}
            fontSize={fontSize}
            fontFamily={element.fontFamily || "Nunito"}
            fill={displayPrefs.highContrast ? "#000000" : (element.fill || "#1e293b")}
          />
        );
        
        const charWidth = fontSize * 0.55;
        currentX += word.length * charWidth;
      }
      
      // Add space after word
      currentX += fontSize * 0.3;
      
      // Check if we need to wrap
      if (currentX > x + width - fontSize * 3) {
        currentX = x;
        currentY += fontSize * (element.lineHeight || 1.6);
        colorIndex = 0; // Reset color for new line
      }
    });
    
    // Line alternation background if enabled
    if (displayPrefs.showLineAlternation) {
      const lineHeight = fontSize * (element.lineHeight || 1.6);
      const numLines = Math.ceil((currentY - y) / lineHeight) + 1;
      const backgrounds = [];
      
      for (let i = 0; i < numLines; i++) {
        if (i % 2 === 1) {
          backgrounds.push(
            <Rect
              key={`${key}-bg-${i}`}
              x={x - 4}
              y={y + i * lineHeight - 2}
              width={width + 8}
              height={lineHeight}
              fill="#fef3c7"
              opacity={0.5}
            />
          );
        }
      }
      
      return (
        <Group key={key}>
          {backgrounds}
          {textElements}
        </Group>
      );
    }
    
    return <Group key={key}>{textElements}</Group>;
  };

  // Render elements on canvas
  const renderElement = (element, index) => {
    const key = element.id || index;
    
    switch (element.type) {
      case "text":
      case "pedagogical_text":
        return renderDifferentiatedText(element, key);
      
      case "rect":
        return (
          <Rect
            key={key}
            x={element.x * scale}
            y={element.y * scale}
            width={(element.width || 100) * scale}
            height={(element.height || 100) * scale}
            fill={element.fill}
            stroke={element.stroke}
            strokeWidth={(element.strokeWidth || 0) * scale}
            cornerRadius={(element.cornerRadius || 0) * scale}
          />
        );
      
      case "circle":
        return (
          <Circle
            key={key}
            x={(element.x + (element.radius || 40)) * scale}
            y={(element.y + (element.radius || 40)) * scale}
            radius={(element.radius || 40) * scale}
            fill={element.fill}
            stroke={element.stroke}
            strokeWidth={(element.strokeWidth || 0) * scale}
          />
        );
      
      case "seyes":
        return renderSeyes(element, key);
      
      case "grid":
        return renderGrid(element, key);
      
      default:
        return null;
    }
  };

  // Render Seyes lines
  const renderSeyes = (element, key) => {
    const lines = [];
    const x = element.x * scale;
    const y = element.y * scale;
    const width = (element.width || 200) * scale;
    const height = (element.height || 120) * scale;
    const lineHeight = (element.lineHeight || 8) * scale;
    
    // Horizontal lines
    for (let i = 0; i <= height; i += lineHeight) {
      const isMainLine = i % (lineHeight * 4) === 0;
      lines.push(
        <Line
          key={`${key}-h-${i}`}
          points={[x, y + i, x + width, y + i]}
          stroke={isMainLine ? "#9333ea" : "#c4b5fd"}
          strokeWidth={isMainLine ? 1 : 0.5}
        />
      );
    }
    
    // Margin line
    if (element.showMargin !== false) {
      lines.push(
        <Line
          key={`${key}-margin`}
          points={[x + 30 * scale, y, x + 30 * scale, y + height]}
          stroke="#f87171"
          strokeWidth={1}
        />
      );
    }
    
    return <Group key={key}>{lines}</Group>;
  };

  // Render grid
  const renderGrid = (element, key) => {
    const lines = [];
    const x = element.x * scale;
    const y = element.y * scale;
    const width = (element.width || 160) * scale;
    const height = (element.height || 160) * scale;
    const cellSize = (element.cellSize || 20) * scale;
    
    // Vertical lines
    for (let i = 0; i <= width; i += cellSize) {
      lines.push(
        <Line
          key={`${key}-v-${i}`}
          points={[x + i, y, x + i, y + height]}
          stroke={element.strokeColor || "#94a3b8"}
          strokeWidth={0.5}
        />
      );
    }
    
    // Horizontal lines
    for (let i = 0; i <= height; i += cellSize) {
      lines.push(
        <Line
          key={`${key}-h-${i}`}
          points={[x, y + i, x + width, y + i]}
          stroke={element.strokeColor || "#94a3b8"}
          strokeWidth={0.5}
        />
      );
    }
    
    return <Group key={key}>{lines}</Group>;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <Skeleton className="w-[300px] h-[400px] rounded-2xl mx-auto mb-4" />
          <p className="text-slate-500">Chargement...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-4">
        <div className="text-center bg-white rounded-2xl p-8 shadow-lg max-w-md">
          <BookOpen className="w-16 h-16 mx-auto mb-4 text-slate-300" />
          <h2 className="text-xl font-bold text-slate-800 mb-2">Oups !</h2>
          <p className="text-slate-500 mb-6">{error}</p>
          <Button onClick={handleGoHome}>
            <Home className="w-4 h-4 mr-2" />
            Retour a l'accueil
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex flex-col">
      {/* Header */}
      <header className="bg-white/90 backdrop-blur-lg border-b border-slate-100 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleGoHome}
            className="flex items-center gap-2"
          >
            <ChevronLeft className="w-4 h-4" />
            <span className="hidden sm:inline">Retour</span>
          </Button>

          <div className="text-center flex-1">
            <h1 className="font-bold text-slate-800 truncate px-4">
              {variant?.master_title || "Ma ressource"}
            </h1>
            {variant?.student_name && (
              <p className="text-xs text-slate-400">
                Version de {variant.student_name}
              </p>
            )}
          </div>

          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowSettings(!showSettings)}
              className={showSettings ? "bg-indigo-100" : ""}
            >
              <Settings className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleFullscreen}
            >
              {isFullscreen ? (
                <Minimize2 className="w-4 h-4" />
              ) : (
                <Maximize2 className="w-4 h-4" />
              )}
            </Button>
          </div>
        </div>
        
        {/* Settings Panel */}
        {showSettings && (
          <div className="bg-white border-t px-4 py-3 space-y-3">
            <p className="text-xs font-medium text-slate-500 uppercase">Aides a la lecture</p>
            <div className="flex flex-wrap gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <Switch
                  checked={displayPrefs.showColoredSyllables}
                  onCheckedChange={(checked) => setDisplayPrefs(p => ({ ...p, showColoredSyllables: checked }))}
                />
                <span className="text-sm">Syllabes colorees</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <Switch
                  checked={displayPrefs.showSilentLetters}
                  onCheckedChange={(checked) => setDisplayPrefs(p => ({ ...p, showSilentLetters: checked }))}
                />
                <span className="text-sm">Lettres muettes</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <Switch
                  checked={displayPrefs.showLineAlternation}
                  onCheckedChange={(checked) => setDisplayPrefs(p => ({ ...p, showLineAlternation: checked }))}
                />
                <span className="text-sm">Lignes alternees</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <Switch
                  checked={displayPrefs.highContrast}
                  onCheckedChange={(checked) => setDisplayPrefs(p => ({ ...p, highContrast: checked }))}
                />
                <span className="text-sm">Contraste eleve</span>
              </label>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-600">Taille du texte:</span>
              <div className="flex gap-1">
                {["small", "normal", "large"].map((size) => (
                  <Button
                    key={size}
                    variant={displayPrefs.fontSize === size ? "default" : "outline"}
                    size="sm"
                    onClick={() => setDisplayPrefs(p => ({ ...p, fontSize: size }))}
                    className="px-3"
                  >
                    {size === "small" ? "A" : size === "normal" ? "A" : "A"}
                    <span className="sr-only">{size}</span>
                  </Button>
                ))}
              </div>
            </div>
          </div>
        )}
      </header>

      {/* Main content */}
      <main 
        ref={containerRef}
        className="flex-1 flex items-center justify-center p-4"
      >
        <div 
          className={`bg-white rounded-2xl shadow-xl overflow-hidden ${displayPrefs.highContrast ? "border-2 border-black" : ""}`}
          style={{ width: dimensions.width, height: dimensions.height }}
        >
          <Stage
            width={dimensions.width}
            height={dimensions.height}
          >
            <Layer>
              {/* Background */}
              <Rect
                x={0}
                y={0}
                width={dimensions.width}
                height={dimensions.height}
                fill={displayPrefs.highContrast ? "#fffff0" : "#ffffff"}
              />
              
              {/* Elements */}
              {variant?.elements?.map((element, index) => renderElement(element, index))}
            </Layer>
          </Stage>
        </div>
      </main>

      {/* Reading aids legend */}
      {displayPrefs.showColoredSyllables && (
        <div className="fixed bottom-20 left-4 bg-white/90 backdrop-blur rounded-lg p-3 shadow-lg border">
          <p className="text-xs font-medium text-slate-500 mb-2">Couleurs des syllabes</p>
          <div className="flex flex-wrap gap-2">
            {SYLLABLE_COLORS.slice(0, 4).map((color, i) => (
              <div key={i} className="flex items-center gap-1">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: color }} />
                <span className="text-xs" style={{ color }}>{i + 1}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Footer with mascot */}
      <div className="fixed bottom-4 right-4 opacity-30 pointer-events-none">
        <img 
          src="/LEcoleDeNavi2.png" 
          alt="" 
          className="h-20"
        />
      </div>
    </div>
  );
}
